import { JsonWebToken } from 'api/JsonWebToken';
import Spinner from 'components/Spinner';
import React, { useEffect, useRef, RefObject, JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

/**
 * Office365 requires a POST request to be submitted via a form.
 * The "user" needs to perform this request. The MS servers return no content
 * to a POST request made via the FETCH API.
 */
function submitOffice365Form(
  formRef: RefObject<HTMLFormElement | null>,
  frameRef: RefObject<HTMLSpanElement | null>,
  isSubmitted: RefObject<boolean>,
): void {
  if (formRef.current && frameRef.current && !isSubmitted.current) {
    const officeFrame = document.createElement('iframe');

    officeFrame.name = 'office_frame';
    officeFrame.id = 'office_frame';
    officeFrame.title = 'Office Frame';
    // This attribute allows true fullscreen mode in slideshow view
    // when using PowerPoint's 'view' action.
    officeFrame.setAttribute('allowfullscreen', 'true');
    officeFrame.className = 'h-full w-full outline-none border-0';

    // The sandbox attribute is needed to allow automatic redirection to the O365 sign-in page in the business user flow
    officeFrame.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox',
    );
    frameRef.current.appendChild(officeFrame);
    formRef.current.requestSubmit();
  }
}

export default function ContentEditorOffice365(): JSX.Element {
  const isSubmitted = useRef<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);
  const frameRef = useRef<HTMLSpanElement>(null);

  const { t } = useTranslation();

  const officeData = useMemo(() => {
    const hash = window.location.hash.substring(1).split('@').map(decodeURIComponent);
    if (hash.length === 3) {
      return {
        url: hash[0] as string,
        token: JsonWebToken.parse(hash[1]),
        favIcon: hash[2] as string,
      };
    }
    return null;
  }, []);

  // In case we have the office data on component mount, we want to submit the form right away.
  // This way the token is send to the Office server and the document will be loaded in the iframe.
  useEffect(() => {
    if (officeData) {
      submitOffice365Form(formRef, frameRef, isSubmitted);
      if (formRef.current && frameRef.current) {
        isSubmitted.current = true;
      }
    }
  }, [officeData]);

  if (officeData?.url && officeData?.token && officeData?.favIcon) {
    return (
      <div className='bg-white h-full w-full outline-none border-0'>
        <Helmet>
          <link rel='icon' href={`${officeData.favIcon}`} media='(prefers-color-scheme: light)' />
        </Helmet>
        <form ref={formRef} id='office_form' name='office_form' target='office_frame' action={`${officeData.url}`} method='post'>
          <input name='access_token' value={`${officeData.token.toString()}`} type='hidden' />
          <input name='access_token_ttl' value={`${officeData.token.expires()}`} type='hidden' />
        </form>
        <span ref={frameRef} id='frameholder' className='h-full w-full outline-none border-0' />
      </div>
    );
  }

  return (
    <div className='bg-white flex justify-center items-center h-full w-full'>
      <div className='flex items-center gap-x-2'>
        <Spinner inverseColor={true} />
        {t('Loading', 'Loading')}...
      </div>
    </div>
  );
}
